/**
 * Form Component
 * The new form component is now responsible for handling form accessibility as well as form status indicator
 *
 */
import { Component, EventEmitter, HostBinding, Inject, Input, Output, ViewChild } from '@angular/core';
import { NgForm, FormsModule } from '@angular/forms';

import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { PipesModule } from '../../pipes/pipes.module';
import { IdService } from '../../services/id.service';
import { FormAccessibilityService } from './accessibility-alert/form-accessibility.service';
import { FormAccessibilityAlertComponent } from './accessibility-alert/form-accessibility-alert.component';

import type { FormRowComponent } from './row/form-row.component';
import type {
  AccessibleErrors,
  FormRowStatus,
  FormType,
  FormValidityStatus,
  InputValidationError
} from './domain/form.domain';

import type { AbstractControl, NgModel } from '@angular/forms';
import type { AfterContentInit, OnDestroy, OnInit } from '@angular/core';

type SubmitTrackingFormControl = AbstractControl & { submitted: boolean };

@Component({
  selector: 'td-wb-form',
  templateUrl: 'form.component.html',
  styleUrls: ['form.component.scss'],
  exportAs: 'FormComponent',
  imports: [FormsModule, FormAccessibilityAlertComponent, PipesModule]
})
export class FormComponent implements OnInit, OnDestroy, AfterContentInit {
  private static readonly HEADER_ID_PREFIX: string = 'td-wb-errors-aria-header';
  @Input() proceedOnError: boolean = false;
  @Input() formType: FormType = 'default';

  @Input()
  set rowStatusIndicator(value: boolean) {
    this._rowStatusIndicator = value;
  }

  get rowStatusIndicator(): boolean {
    return this._rowStatusIndicator;
  }

  @Output() readonly statusChanges: EventEmitter<FormValidityStatus> = new EventEmitter<FormValidityStatus>();
  @Output() readonly rowStatus: EventEmitter<FormRowStatus> = new EventEmitter<FormRowStatus>();
  @Output() readonly submitAction: EventEmitter<NgForm> = new EventEmitter<NgForm>();
  @ViewChild(NgForm, { static: true }) formElement: NgForm;
  @HostBinding('class.td-wb-form--narrative') isNarrative: boolean = false;

  accessibleHeaderId: string;
  accessibleErrors: AccessibleErrors = {};

  displayedAccessibleErrors: Array<string>;

  readonly accessibleErrorsSubject: BehaviorSubject<Array<string>> = new BehaviorSubject<Array<string>>([]);

  private formSubmitted: boolean = false;
  private readonly displayedErrorsSubject: BehaviorSubject<AccessibleErrors> = new BehaviorSubject<AccessibleErrors>({});
  private rows: Array<FormRowComponent> = [];
  private rowId: number = 0;
  private focusedRowId: number = -1;
  private _rowStatusIndicator: boolean = false;
  private readonly destroy: Subject<void> = new Subject<void>();
  private readonly isModalForm: boolean = false;

  constructor(@Inject(IdService) private idService: IdService,
              @Inject(FormAccessibilityService) private formAccessibilityService: FormAccessibilityService) {
    this.isModalForm = this.formAccessibilityService.isModal();
  }

  ngOnInit(): void {
    // create a unique id for the form for accessibility purposes
    this.accessibleHeaderId = this.idService.generateNamespaceSequentialId(FormComponent.HEADER_ID_PREFIX);
    this.isNarrative = this.formType === 'narrative';
    this.formAccessibilityService.registerErrors(this.displayedErrorsSubject, this.isModalForm);
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
    this.displayedErrorsSubject.next({});
    this.displayedErrorsSubject.complete();
    this.formAccessibilityService.unregisterErrors(this.isModalForm);
  }

  ngAfterContentInit(): void {
    this.formElement.statusChanges
      .pipe(takeUntil(this.destroy))
      .subscribe((status: FormValidityStatus): void => {
        this.statusChanges.emit(status);
      });
  }

  get pristine(): boolean {
    return this.formElement !== null && this.formElement.pristine;
  }

  get valid(): boolean {
    return this.formElement !== null && this.formElement.valid;
  }

  get invalid(): boolean {
    return this.formElement !== null && this.formElement.invalid;
  }

  get submitted(): boolean {
    return this.formSubmitted;
  }

  setAccessibleError(inputName: string, error?: InputValidationError): void {
    const key = FormAccessibilityService.getErrorKey(inputName);
    if (error !== undefined) {
      this.accessibleErrors[key] = error;
    } else {
      delete this.accessibleErrors[key];
    }
    this.onErrorCountChange();
  }

  hasErrors(): boolean {
    return this.getVisibleErrorCount() > 0;
  }

  getVisibleErrorCount(): number {
    return this.formAccessibilityService.getVisibleErrorCount(this.isModalForm);
  }

  onErrorCountChange(): void {
    this.displayedAccessibleErrors = Object.keys(this.accessibleErrors)
      .filter((error: string): boolean => this.accessibleErrors[error].isDisplayed);
    this.accessibleErrorsSubject.next(this.displayedAccessibleErrors);
    this.displayedErrorsSubject.next(this.accessibleErrors);
  }

  addRow(row: FormRowComponent): FormRowComponent {
    row.id = this.rowId++;
    this.rows.push(row);
    return row;
  }

  removeRow(row: FormRowComponent): void {
    const rowIndex = this.rows.findIndex((existingRow: FormRowComponent) => existingRow?.id === row.id);
    if (rowIndex !== -1) {
      if (this.focusedRowId === row.id) {
        this.focusedRowId = -1;
      }
      this.rows.splice(rowIndex, 1);
    }
  }

  isFormSubmitted(): boolean {
    return this.formSubmitted;
  }

  submit(): boolean {
    return this.maybeSubmitForm();
  }

  reset(value?: unknown): void {
    this.resetForm(value);
    this.formSubmitted = false;
  }

  // deprecated - use submit
  maybeSubmitForm(): boolean {
    if (this.formElement.invalid) {
      // (this.formElement as { submitted: boolean }).submitted = true; // NOTE: work around readonly flag
      this.formSubmitted = true;
      Object.keys(this.formElement.form.controls).forEach((control: string): void => {
        const formControl = this.formElement.form.get(control);
        this.markAsSubmitted(formControl as SubmitTrackingFormControl);
        formControl.markAsDirty();
        formControl.markAsTouched();
        formControl.updateValueAndValidity({
          emitEvent: true
        });
        this.formAccessibilityService.scrollErrorIntoView(this.isModalForm);
      });
    }
    if (this.formElement.valid || this.proceedOnError) {
      this.submitAction.emit(this.formElement);
    }
    return this.formElement.valid;
  }

  resetForm(value?: unknown): void {
    this.formElement.resetForm(value);
    if (this.formSubmitted) {
      Object.keys(this.formElement.form.controls).forEach((control: string): void => {
        const formControl = this.formElement.form.get(control);
        this.markAsUnsubmitted(formControl as SubmitTrackingFormControl);
      });
    }
  }

  setDirty(): void {
    this.formElement.form.markAsDirty();
    this.formElement.form.markAsTouched();
  }

  setPristine(): void {
    this.formElement.form.markAsPristine();
    this.formElement.form.markAsUntouched();
  }

  registerFormControl(inputModel: NgModel): void {
    this.formElement.addControl(inputModel);
  }

  unRegisterFormControl(inputModel: NgModel): void {
    this.formElement.removeControl(inputModel);
  }

  setRowStatus(rowStatus: FormRowStatus): void {
    this.rowStatus.emit(rowStatus);
  }

  private markAsSubmitted(formControl: SubmitTrackingFormControl): void {
    formControl.submitted = true;
  }

  private markAsUnsubmitted(formControl: SubmitTrackingFormControl): void {
    delete formControl.submitted;
  }
}
